import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Props, Emits } from '../type'
import type { Ref, ComputedRef } from 'vue'
import type { NotificationMeta } from '@/types/domain/Notification'
import type { DisplayModeType } from '@/constants/enums/NotificationPanelDisplayMode'

import { logout } from '@/composables/api/customer/Auth'
import { DisplayMode } from '@/constants/enums/NotificationPanelDisplayMode'

type UseNotificationPanel = (
  props: Props,
  emit: Emits,
  openConfirmLogoutModal: () => void,
) => {
  currentNotificationMeta: Ref<NotificationMeta | undefined>
  linkUrl: Ref<string | undefined>
  unreadCount: ComputedRef<number>
  displayedNotificationMetaList: ComputedRef<NotificationMeta[]>
  notificationMetaList: ComputedRef<NotificationMeta[]>
  toggleUnreadOnlyDisplay: () => void
  changeDisplayMode: (mode: DisplayModeType) => void
  changeDetailDisplay: (notificationMeta: NotificationMeta) => void
  confirmOpenLink: (url: string) => void
  openLinkAndLogout: () => Promise<void>
}

export const useNotificationPanel: UseNotificationPanel = (
  props,
  emit,
  openConfirmLogoutModal,
) => {
  const { t } = useI18n()

  const currentNotificationMeta = ref<NotificationMeta | undefined>(undefined)
  const linkUrl = ref<string | undefined>(undefined)

  const notificationMetaList = computed<NotificationMeta[]>(() => {
    return props.notificationMetaList || []
  })

  const unreadCount = computed(() => {
    return notificationMetaList.value.filter(notification => !notification.isRead).length
  })

  const displayedNotificationMetaList = computed(() => {
    return notificationMetaList.value.filter((notification) => {
      if (props.isUnreadOnlyDisplay) {
        return !notification.isRead
      }
      return true
    })
  })

  const toggleUnreadOnlyDisplay = () => {
    emit('toggle-unread-only-display')
  }

  const changeDetailDisplay = (notificationMeta: NotificationMeta) => {
    currentNotificationMeta.value = notificationMeta
    if (currentNotificationMeta.value) {
      changeDisplayMode(DisplayMode.DETAIL)
    }
  }

  const changeDisplayMode = (mode: DisplayModeType) => {
    emit('change-display-mode', mode)
  }

  const confirmOpenLink = (url: string) => {
    linkUrl.value = url
    openConfirmLogoutModal()
  }

  const openLinkAndLogout = async () => {
    if (linkUrl.value) {
      await logout()
      try {
        window.open(linkUrl.value, '_self')
      }
      catch {
        alert(t('common.error.code_9999'))
      }
    }
  }

  return {
    currentNotificationMeta,
    linkUrl,
    unreadCount,
    displayedNotificationMetaList,
    notificationMetaList,
    toggleUnreadOnlyDisplay,
    changeDetailDisplay,
    changeDisplayMode,
    confirmOpenLink,
    openLinkAndLogout,
  }
}
